import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "@/lib/api";
import { Crosshair } from "@/components/Crosshair";
import { timeAgo } from "@/lib/constants";
import { FileWarning, Check, User, Tag, MessageSquareText, ShieldAlert, Trash2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "@/lib/toast";
import ReportsPanel from "@/pages/admin/ReportsPanel";
import { LoadFailed } from "@/components/LoadState";

const SCREEN_TABS = [
  ["open", "Open"],
  ["resolved", "Resolved"],
  ["all", "All"],
];

// Human-friendly labels for the `field` that was flagged.
const FIELD_LABEL = {
  bio: "Profile bio",
  display_name: "Display name",
  username: "Username",
  listing_title: "Listing title",
  listing_description: "Listing description",
  listing: "Listing",
  forum_thread: "Forum thread",
  forum_reply: "Forum reply",
  internal_message: "Internal Message",
};

// Which staff→member tool an internal-message flag came from.
const CHANNEL_LABEL = {
  support_dm: "Support DM",
  mailbox: "Support Mailbox",
};

// The automated content-screening queue (bios / names / listing text routed to a human).
function ScreeningPanel({ onChanged }) {
  const [tab, setTab] = useState("open");
  const [items, setItems] = useState([]);
  const [openCount, setOpenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [reasons, setReasons] = useState({});
  const [loadErr, setLoadErr] = useState(null);

  const load = useCallback((status) => {
    setLoading(true);
    setLoadErr(null);
    api.get(`/admin/content-review?status=${status}`)
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => setLoadErr(formatError(err?.response?.data?.detail) || "Couldn't load the screening queue."))
      .finally(() => setLoading(false));
    api.get("/admin/content-review?status=open")
      .then(({ data }) => setOpenCount((data.items || []).length))
      .catch(() => {});
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  const resolve = async (id, outcome) => {
    const reason = (reasons[id] || "").trim();  // optional note — resolving is one click, no gate
    setBusyId(id);
    try {
      const { data } = await api.post(`/admin/content-review/${id}/resolve`, { outcome, reason });
      let msg;
      if (outcome === "kept") msg = "Kept — flag cleared.";
      else if (data?.removed) msg = data?.warned ? "Content removed & member warned." : "Content removed.";
      else msg = data?.warned ? "Member warned — content flagged for manual removal." : "Flagged for manual removal.";
      toast.success(msg);
      setReasons((r) => { const n = { ...r }; delete n[id]; return n; });
      load(tab);
      onChanged?.();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Couldn't resolve this flag. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="content-review-screening">
      <div className="inline-flex border border-sniper bg-surface p-1 mb-5">
        {SCREEN_TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`content-review-tab-${id}`}
            className={`px-5 py-2 font-mono uppercase text-xs tracking-wide transition-colors ${tab === id ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
            {label}{id === "open" && openCount > 0 ? ` (${openCount})` : ""}
          </button>
        ))}
      </div>

      <div className="space-y-3" data-testid="content-review-list">
        {loading && <p className="text-sm font-body text-muted-foreground border border-sniper bg-surface p-6">Loading…</p>}
        {!loading && loadErr && <LoadFailed reason={loadErr} onRetry={() => load(tab)} testid="content-review-load-failed" />}
        {!loading && !loadErr && items.length === 0 && (
          <p className="text-sm font-body text-muted-foreground border border-sniper bg-surface p-6" data-testid="content-review-empty">
            Nothing {tab === "all" ? "" : tab} to review. This queue is clear.
          </p>
        )}
        {!loading && !loadErr && items.map((it) => (
          <div key={it.review_id} className="border border-sniper bg-surface p-4" data-testid={`content-review-item-${it.review_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-mono uppercase text-muted-foreground">
                  <span className="inline-flex items-center gap-1 text-olive-bright">
                    <MessageSquareText className="w-3 h-3" /> {FIELD_LABEL[it.field] || it.field}
                  </span>
                  {it.source === "internal_message" && (
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] uppercase border border-volt/50 text-volt px-1.5 py-0.5" data-testid={`content-review-internal-badge-${it.review_id}`}>
                      <ShieldAlert className="w-2.5 h-2.5" /> Staff → Member{CHANNEL_LABEL[it.channel] ? ` · ${CHANNEL_LABEL[it.channel]}` : ""}
                    </span>
                  )}
                  {it.username && (
                    <a href={`/u/${it.username}`} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:text-foreground transition-colors" data-testid={`content-review-user-${it.review_id}`}>
                      <User className="w-3 h-3" /> {it.source === "internal_message" ? "sent by " : ""}@{it.username}
                    </a>
                  )}
                  {it.source === "internal_message" && it.recipient_username && (
                    <span data-testid={`content-review-recipient-${it.review_id}`}>to {it.recipient_username}</span>
                  )}
                  <span>{timeAgo(it.created_at)}</span>
                  {it.link && (
                    <a href={it.link} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 text-olive-bright hover:text-foreground transition-colors" data-testid={`content-review-link-${it.review_id}`}>
                      <ExternalLink className="w-3 h-3" /> View
                    </a>
                  )}
                  {it.status !== "open" && (
                    <span className="text-teal-300">· {it.outcome || "resolved"}{it.resolved_by ? ` by @${it.resolved_by}` : ""}</span>
                  )}
                </div>
                <p className="mt-2 font-body text-sm text-foreground bg-input border border-sniper px-3 py-2 break-words whitespace-pre-wrap" data-testid={`content-review-value-${it.review_id}`}>
                  {it.value || <span className="text-muted-foreground">(empty)</span>}
                </p>
                {(it.categories?.length > 0 || it.why) && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {(it.categories || []).map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase border border-amber-400/50 text-amber-400 px-1.5 py-0.5">
                        <Tag className="w-2.5 h-2.5" /> {c}
                      </span>
                    ))}
                    {it.why && <span className="font-body text-xs text-secondary-foreground">{it.why}</span>}
                  </div>
                )}
                {it.status !== "open" && (it.reason || it.action_taken) && (
                  <p className="mt-2 text-[11px] font-mono text-muted-foreground border-l-2 border-teal-300/50 pl-2" data-testid={`content-review-resolution-${it.review_id}`}>
                    {it.action_taken && <span className="text-teal-300 uppercase">{it.action_taken}</span>}
                    {it.reason && <span className="text-secondary-foreground"> — {it.reason}</span>}
                  </p>
                )}
              </div>
            </div>
            {it.status === "open" && (
              <div className="mt-3 pt-3 border-t border-sniper space-y-2.5" data-testid={`content-review-resolve-form-${it.review_id}`}>
                <input
                  value={reasons[it.review_id] || ""}
                  onChange={(e) => setReasons((r) => ({ ...r, [it.review_id]: e.target.value }))}
                  data-testid={`content-review-reason-${it.review_id}`}
                  placeholder="Optional note — saved to the audit log (e.g. acceptable slang / cleared profanity)"
                  className="w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-body text-sm transition-colors"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => resolve(it.review_id, "kept")} disabled={busyId === it.review_id}
                    data-testid={`content-review-keep-${it.review_id}`}
                    className="inline-flex items-center gap-1 border border-olive/60 text-olive-bright hover:bg-olive/10 disabled:opacity-50 font-heading font-bold uppercase text-sm px-3.5 py-2 transition-colors">
                    <Check className="w-4 h-4" /> {it.field === "listing" ? "Approve Listing" : "Keep"}
                  </button>
                  <button onClick={() => resolve(it.review_id, "cleared")} disabled={busyId === it.review_id}
                    data-testid={`content-review-clear-${it.review_id}`}
                    className="inline-flex items-center gap-1 border border-blaze/50 text-blaze hover:bg-blaze/10 disabled:opacity-50 font-heading font-bold uppercase text-sm px-3.5 py-2 transition-colors">
                    <Trash2 className="w-4 h-4" /> Clear content
                  </button>
                  <button onClick={() => resolve(it.review_id, "warned")} disabled={busyId === it.review_id}
                    data-testid={`content-review-warn-${it.review_id}`}
                    className="inline-flex items-center gap-1 border border-volt/50 text-volt hover:bg-volt/10 disabled:opacity-50 font-heading font-bold uppercase text-sm px-3.5 py-2 transition-colors">
                    <AlertTriangle className="w-4 h-4" /> Clear + Warn
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Content Review now hosts TWO queues: member-filed Reports (formerly the standalone
// Moderation page) and the automated Screening queue.
export default function AdminContentReview() {
  const [view, setView] = useState("reports");
  const [counts, setCounts] = useState({ reports: 0, screening: 0 });

  const refreshCounts = useCallback(() => {
    api.get("/admin/reports?status=open").then(({ data }) => setCounts((c) => ({ ...c, reports: (data.reports || []).length }))).catch(() => {});
    api.get("/admin/content-review?status=open").then(({ data }) => setCounts((c) => ({ ...c, screening: (data.items || []).length }))).catch(() => {});
  }, []);

  useEffect(() => { refreshCounts(); }, [refreshCounts]);

  const VIEWS = [
    { id: "reports", label: "Reports", count: counts.reports, icon: ShieldAlert },
    { id: "screening", label: "Screening", count: counts.screening, icon: FileWarning },
  ];

  return (
    <div className="text-foreground" data-testid="admin-content-review">
      <div className="flex items-center gap-2">
        <FileWarning className="w-6 h-6 text-amber-400" strokeWidth={1.6} />
        <h1 className="font-heading text-4xl font-bold uppercase tracking-tight" data-testid="content-review-title">Content Review</h1>
      </div>
      <p className="text-sm font-body text-secondary-foreground mt-2">
        Member-filed <span className="text-foreground">Reports</span> and the automated <span className="text-foreground">Screening</span> queue
        (bios, names, listing text the pipeline routed to a human — never auto-blocked) live here.
        <span className="text-foreground ml-1" data-testid="content-review-open-count">{counts.reports + counts.screening} open</span> across both.
      </p>
      <Crosshair className="my-5" />

      <div className="inline-flex border border-sniper bg-surface p-1 mb-5" data-testid="content-review-views">
        {VIEWS.map((v) => {
          const Icon = v.icon;
          return (
            <button key={v.id} onClick={() => setView(v.id)} data-testid={`cr-view-${v.id}`}
              className={`inline-flex items-center gap-2 px-5 py-2 font-heading uppercase text-sm tracking-tight transition-colors ${view === v.id ? "bg-olive text-white" : "text-secondary-foreground hover:text-white"}`}>
              <Icon className="w-4 h-4" /> {v.label}
              {v.count > 0 && <span className={`font-mono text-[10px] tabular-nums px-1.5 py-0.5 ${view === v.id ? "bg-black/25" : "bg-surface-2 text-muted-foreground"}`}>{v.count}</span>}
            </button>
          );
        })}
      </div>

      {view === "reports" ? <ReportsPanel onChanged={refreshCounts} /> : <ScreeningPanel onChanged={refreshCounts} />}
    </div>
  );
}
